import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Alert, Tab, Tabs, Badge, Collapse } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getAllCompaniesPanels } from '../../api/companyApi';
import CompanyUpload from '../../components/admin/CompanyUpload';

const ManageCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('list');
  
  // State to track which company details are expanded
  const [expandedCompany, setExpandedCompany] = useState(null);
  // State to track which panels are expanded for each round
  const [expandedPanels, setExpandedPanels] = useState({});

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAllCompaniesPanels();
      setCompanies(data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Failed to fetch companies. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchCompanies();
    toast.info('Refreshing company data...');
  };

  const toggleCompanyDetails = (companyId) => {
    setExpandedCompany(expandedCompany === companyId ? null : companyId);
  };

  const togglePanelsDetails = (companyId, roundNumber) => {
    const key = `${companyId}-${roundNumber}`;
    setExpandedPanels(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (timeString) => {
    return timeString ? new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  };

  return (
    <Container className="py-4">
      <h1 className="mb-4">Manage Companies</h1>
      
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab eventKey="list" title="Company List">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">All Companies</h5>
              <Button variant="outline-primary" size="sm" onClick={handleRefresh}>
                Refresh
              </Button>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading companies...</p>
                </div>
              ) : companies.length === 0 ? (
                <Alert variant="info">
                  No companies found. Use the "Upload Companies" tab to add companies.
                </Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Company Name</th>
                        <th>Visit Date</th>
                        <th>Rounds</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {companies.map((company) => (
                        <React.Fragment key={company.id}>
                          <tr>
                            <td>{company.name}</td>
                            <td>{formatDate(company.visit_date)}</td>
                            <td>
                              {company.rounds?.length ? (
                                <Badge bg="info">{company.rounds.length} Rounds</Badge>
                              ) : (
                                <Badge bg="secondary">No Rounds</Badge>
                              )}
                            </td>
                            <td>
                              <Button 
                                variant="outline-secondary" 
                                size="sm"
                                onClick={() => toggleCompanyDetails(company.id)}
                                aria-expanded={expandedCompany === company.id}
                              >
                                {expandedCompany === company.id ? 'Hide Details' : 'View Details'}
                              </Button>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="4" className="p-0">
                              <Collapse in={expandedCompany === company.id}>
                                <div>
                                  <Card className="border-info m-2">
                                    <Card.Header className="bg-info text-white">
                                      <h5 className="mb-0">{company.name} - Details</h5>
                                    </Card.Header>
                                    <Card.Body>
                                      <h6>Rounds Information</h6>
                                      
                                      {company.rounds?.length === 0 ? (
                                        <Alert variant="info">No rounds configured for this company.</Alert>
                                      ) : (
                                        <Table striped bordered size="sm">
                                          <thead>
                                            <tr>
                                              <th>Round</th>
                                              <th>Time</th>
                                              <th>Duration</th>
                                              <th>Panels</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {company.rounds?.map((round) => (
                                              <tr key={`round-${company.id}-${round.round_number}`}>
                                                <td>Round {round.round_number}</td>
                                                <td>
                                                  {formatTime(round.start_time)} - {formatTime(round.end_time)}
                                                </td>
                                                <td>{round.duration} mins</td>
                                                <td>
                                                  <Button 
                                                    variant="outline-secondary" 
                                                    size="sm"
                                                    onClick={() => togglePanelsDetails(company.id, round.round_number)}
                                                    aria-expanded={expandedPanels[`${company.id}-${round.round_number}`]}
                                                  >
                                                    {round.panels?.length || 0} Panels
                                                  </Button>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </Table>
                                      )}
                                      
                                      {/* Panels Details */}
                                      {company.rounds?.map((round) => (
                                        <Collapse
                                          key={`panels-section-${company.id}-${round.round_number}`}
                                          in={expandedPanels[`${company.id}-${round.round_number}`]}
                                        >
                                          <div className="mt-3">
                                            <Card className="border-secondary">
                                              <Card.Header className="bg-secondary text-white">
                                                <h6 className="mb-0">Round {round.round_number} - Panels</h6>
                                              </Card.Header>
                                              <Card.Body>
                                                {round.panels?.length === 0 ? (
                                                  <Alert variant="info">No panels configured for this round.</Alert>
                                                ) : (
                                                  <Table striped bordered size="sm">
                                                    <thead>
                                                      <tr>
                                                        <th>Panel Number</th>
                                                        <th>Interviewer</th>
                                                        <th>Location</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {round.panels?.map((panel) => (
                                                        <tr key={`panel-${company.id}-${round.round_number}-${panel.panel_number}`}>
                                                          <td>Panel {panel.panel_number}</td>
                                                          <td>{panel.panel_interviewe_name}</td>
                                                          <td>{panel.panel_place}</td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </Table>
                                                )}
                                              </Card.Body>
                                            </Card>
                                          </div>
                                        </Collapse>
                                      ))}
                                    </Card.Body>
                                  </Card>
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="upload" title="Upload Companies">
          <CompanyUpload onUploadSuccess={fetchCompanies} />
        </Tab>
      </Tabs>
    </Container>
  );
};

export default ManageCompanies;